import React from "react";
import {
  Download,
  File as FileIcon,
  FileArchive,
  FileImage,
  FileSpreadsheet,
  FileText,
  Loader2,
  type LucideIcon,
} from "lucide-react";
import { Attachment } from "../TicketDetail";

/* eslint-disable react/prop-types */ // TS > PropTypes

export const getAttLabel = (att: Attachment) =>
  att.filename || att.name || "Attachment";

export type DownloadMap = Record<string | number, boolean>;

/** Splits "report.final.pdf" into "report.final" and "pdf". */
export function splitFileName(name: string): { base: string; ext: string } {
  const dot = name.lastIndexOf(".");
  if (dot <= 0 || dot === name.length - 1) return { base: name, ext: "" };
  return { base: name.slice(0, dot), ext: name.slice(dot + 1).toLowerCase() };
}

/** "245 KB", "1.2 MB"; empty when the endpoint did not send a size. */
export function formatFileSize(bytes?: number): string {
  if (!bytes || bytes <= 0) return "";
  const units = ["B", "KB", "MB", "GB"];
  const exponent = Math.min(
    Math.floor(Math.log(bytes) / Math.log(1024)),
    units.length - 1
  );
  const value = bytes / 1024 ** exponent;
  const shown = exponent === 0 ? value : value.toFixed(value < 10 ? 1 : 0);
  return `${shown} ${units[exponent]}`;
}

const ICON_BY_EXT: Record<string, LucideIcon> = {
  png: FileImage,
  jpg: FileImage,
  jpeg: FileImage,
  gif: FileImage,
  webp: FileImage,
  svg: FileImage,
  pdf: FileText,
  doc: FileText,
  docx: FileText,
  txt: FileText,
  log: FileText,
  csv: FileSpreadsheet,
  xls: FileSpreadsheet,
  xlsx: FileSpreadsheet,
  zip: FileArchive,
  gz: FileArchive,
  rar: FileArchive,
};

/** The icon for a file's type, by extension; a plain file when unknown. */
export function getFileIcon(ext: string): LucideIcon {
  return ICON_BY_EXT[ext] ?? FileIcon;
}

type AttachmentChipProps = {
  att: Attachment;
  downloading?: boolean;
  onDownload: (att: Attachment) => void | Promise<void>;
  className?: string;
};

/**
 * One attachment as a file row (CTX-1121): an icon for its type, the name
 * with its extension always visible (a long name truncates the base, never
 * the extension), the size when known, and a quiet download action instead
 * of a filled brand button.
 */
export const AttachmentChip = React.memo(function AttachmentChip({
  att,
  downloading = false,
  onDownload,
  className = "",
}: AttachmentChipProps) {
  const label = getAttLabel(att);
  const { base, ext } = splitFileName(label);
  const Icon = getFileIcon(ext);
  const meta = [ext.toUpperCase(), formatFileSize(att.size)]
    .filter(Boolean)
    .join(" · ");

  return (
    <div
      className={[
        "flex min-w-0 items-center gap-3 rounded-md border border-semantic-border-primary",
        "bg-semantic-bg-primary px-3 py-2 transition-colors hover:bg-semantic-state-active",
        className,
      ].join(" ")}
      title={label}
    >
      <span className="flex h-8 w-8 flex-none items-center justify-center rounded bg-semantic-bg-tertiary text-semantic-text-secondary">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex min-w-0 text-sm text-semantic-text-primary">
          <span className="truncate">{base}</span>
          {ext && <span className="flex-none">.{ext}</span>}
        </span>
        {meta && (
          <span className="block text-xs text-semantic-text-secondary">
            {meta}
          </span>
        )}
      </span>
      <button
        type="button"
        onClick={() => onDownload(att)}
        disabled={downloading}
        aria-label={`Download ${label}`}
        className="flex h-8 w-8 flex-none items-center justify-center rounded text-semantic-text-brand transition-colors hover:bg-semantic-bg-brand-secondary disabled:cursor-wait disabled:opacity-60"
      >
        {downloading ? (
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
        ) : (
          <Download className="h-4 w-4" aria-hidden="true" />
        )}
      </button>
    </div>
  );
});

type AttachmentListProps = {
  attachments: Attachment[];
  downloadingMap?: DownloadMap;
  onDownload: (att: Attachment) => void | Promise<void>;
  columns?: 1 | 2; // explicit to avoid Tailwind purge issues
  className?: string;
};

export const AttachmentList: React.FC<AttachmentListProps> = ({
  attachments,
  downloadingMap = {},
  onDownload,
  columns = 2,
  className = "",
}) => {
  const gridCols =
    columns === 1 ? "grid-cols-1" : "grid-cols-1 sm:grid-cols-2";

  // No frame of its own: it always sits inside a ticket card (CTX-1121).
  return (
    <div className={["grid gap-2", gridCols, className].join(" ")}>
      {attachments.map((att) => (
        <AttachmentChip
          key={att.id}
          att={att}
          downloading={!!downloadingMap[att.id]}
          onDownload={onDownload}
        />
      ))}
    </div>
  );
};
